<template>
  <div :class="getClasses">
    <template v-if="isAddLocationFlow"></template>
    <template v-else-if="!isCreateFlow">
      <div
        v-if="isSearchExpanded"
        v-on-click-outside="handleSearchClickOutside"
        :class="['search-expanded-wrapper', { 'picker-mode': isPickerMode }]"
      >
        <GamSimpleLocationSearch v-bind="getSearchConfig" />
      </div>

      <div v-if="showAllSpots" class="spot-toggle-row">
        <GamButton v-if="!isSearchExpanded && !mapStore.proposalMode" class="search-btn" v-bind="getSearchButton" />
        <GamButton v-if="!isPickerMode" v-bind="getCloseShowAllButton" />
      </div>
      <GamButton v-if="showAllSpots && !isPickerMode" v-bind="getAddLocationButton" />
      <GamButton v-if="!showAllSpots" v-bind="getShowAllButton" />
    </template>
  </div>
</template>

<script setup lang="ts">
import type { GeoCoords } from '@/core/data/location/location.interface';
import { MapLocationSource } from '@/core/data/location/location.type';
import { gambits } from '@/core/gambits';
import LocationHelper from '@/core/helpers/location.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import type { Result } from '@/core/network/http/httpClient.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useMapStore } from '@/stores/map.store';
import GamButton from '@/views/components/GamButton.vue';
import GamSimpleLocationSearch from '@/views/components/GamSimpleLocationSearch.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamDropdownCategory, GamDropdownSize } from '@/views/composables/constants/components/gamDropdown.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GamSimpleInputSearchType } from '@/views/composables/models/components/GamInput';
import { vOnClickOutside } from '@vueuse/components';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const router = useRouter();
const route = useRoute();

const mapStore = useMapStore();
const { showAllSpots } = storeToRefs(mapStore);

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const isPickerMode = computed(() => mapStore.proposalMode);

const isCreateFlow = computed(
  () =>
    route.query.side_panel === 'add_location' &&
    (!isMobile.value || (isMobile.value && route.query.mobile_step === 'pick_coordinate')),
);
const isAddLocationFlow = computed(
  () =>
    route.query.side_panel === 'add_location' &&
    (!isMobile.value || (isMobile.value && route.query.mobile_step === 'pick_coordinate')),
);

const getClasses = computed(() => ({
  'mod-actions': true,
  'mod-actions-guest': !user.value,
}));

// --- Search ---

const isSearchExpanded = ref(false);
let justExpanded = false;

const expandSearch = () => {
  justExpanded = true;
  isSearchExpanded.value = true;
  mapStore.searchPin = null;
  mapStore.isSearchExpanded = true;
  requestAnimationFrame(() => {
    justExpanded = false;
  });
};

const collapseSearch = () => {
  isSearchExpanded.value = false;
  mapStore.isSearchExpanded = false;
};

const onSearchMounted = (el: HTMLInputElement) => {
  nextTick(() => el.focus());
};

const handleSearchClickOutside = () => {
  if (!isSearchExpanded.value || justExpanded) return;
  collapseSearch();
};

const onSearchSelected = (loc: GamDropdownItem | null) => {
  if (!loc) return;

  const vLow = (loc as any).viewportLow as GeoCoords | undefined;
  const vHigh = (loc as any).viewportHigh as GeoCoords | undefined;
  const isCityResult =
    loc.source !== 'internal' &&
    vLow &&
    vHigh &&
    (Math.abs(vHigh.lat - vLow.lat) > 0.05 || Math.abs(vHigh.lng - vLow.lng) > 0.05);

  if (loc.source === 'internal') {
    mapStore.searchPin = null;
  } else if (isCityResult) {
    mapStore.searchPin = null;
  } else if (loc.coordinates) {
    mapStore.searchPin = loc.coordinates;
    if (!isPickerMode.value) {
      const isPoi = loc.id?.indexOf('poi') > -1;
      mapStore.setNewLocation({
        name: loc.label,
        label: loc.label,
        subLabel: loc.subLabel,
        address: isPoi ? loc.subLabel : `${loc.label}, ${loc.subLabel}`,
        coordinates: loc.coordinates,
        googlePlaceId: loc.id,
        source: loc.source as MapLocationSource,
      });
    }
  }

  if (loc.coordinates) {
    // @ts-ignore
    const gMap = window.gMap?.map;
    if (gMap) {
      if (isCityResult) {
        gMap.panTo(loc.coordinates);
        gMap.setZoom(12);
      } else {
        gMap.setZoom(17);
        gMap.panTo(loc.coordinates);
      }
    }
  }

  collapseSearch();
};

const searchId = GamListId.LOCATION_SEARCH;

const getSearchConfig = computed(
  (): GamSimpleInputSearchType<GamDropdownItem> => ({
    id: searchId,
    isSearch: true,
    input: {
      name: GamInputName.SEARCH,
      placeholder: 'locations.map.search.placeholder',
      leftIcon: GamIconName.SEARCH,
      canClear: true,
    },
    dropdown: {
      id: searchId,
      category: GamDropdownCategory.LOCATIONS,
      size: GamDropdownSize.LARGE,
      itemSpacing: 0,
      reversed: false,
    },
    selectCallback: onSearchSelected,
    loadCallback: async (query: string): Promise<Result<GamDropdownItem[]>> => {
      const result = await gambits.locationService.searchLocationsByQuery(query, {
        mapbox: true,
        internal: true,
      });
      return {
        ...result,
        data: LocationHelper.mapSearchLocations(result.data),
      };
    },
    onMounted: onSearchMounted,
  }),
);

const getSearchButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.SEARCH,
    variant: GamButtonVariant.STEKLO,
    size: GamButtonSize.LARGE,
    isIconOnly: true,
    onClick: expandSearch,
  };
});

// --- End search ---

const handleAddLocation = () => {
  router.push({
    ...route,
    query: {
      ...route.query,
      side_panel: 'add_location',
      mobile_step: isMobile.value ? 'pick_coordinate' : undefined,
    },
  });
};

const getShowAllButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.LOCATION_EMPTY,
    variant: GamButtonVariant.STEKLO,
    size: GamButtonSize.LARGE,
    isIconOnly: true,
    onClick: () => mapStore.toggleShowAllSpots(),
  };
});

const getCloseShowAllButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.X_CLOSE,
    variant: GamButtonVariant.STEKLO,
    size: GamButtonSize.LARGE,
    isIconOnly: true,
    onClick: () => {
      collapseSearch();
      mapStore.toggleShowAllSpots();
    },
  };
});

const getAddLocationButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.PLUS,
    variant: GamButtonVariant.STEKLO,
    size: GamButtonSize.LARGE,
    isIconOnly: true,
    onClick: handleAddLocation,
  };
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.mod-actions {
  position: relative;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;

  pointer-events: none;

  > :deep(.gam-button) {
    pointer-events: auto;
  }

  > :deep(.gam-button) > .gam-button-container,
  .spot-toggle-row :deep(.gam-button-container) {
    width: 3rem;
    height: 3rem;
    min-width: 3rem;
    min-height: 3rem;
    border-radius: 3rem;
    border: 1px solid var(--color-white-10);
    backdrop-filter: blur(8px);
    background-color: var(--color-dark-5);
    color: var(--color-white-70);
    transition: all 200ms linear;

    @include hover {
      background-color: var(--color-dark-20);
      border-color: var(--color-white-30);
      color: var(--color-white);
    }
  }
}

.spot-toggle-row {
  display: flex;
  flex-direction: row;
  gap: var(--spacing-md);
  align-items: center;
}

.search-expanded-wrapper {
  position: absolute;
  top: 0;
  right: 56px;
  width: 300px;
  max-width: calc(100vw - 8rem);
  pointer-events: auto;
  z-index: 10;

  &.picker-mode {
    right: 0;
  }
}

.is-mobile .mod-actions-guest {
  transform: translateY(calc((1rem + var(--spacing-l)) * -1));
}
</style>
